// The offer's own $0 magnet sheet in the cart (issue #5218 item 9): "Free Photo
// Magnets (offer) — $0", so the parent sees the deal survive to checkout.
//
// Matched by NAME -- the same contract the server uses (Addon::FREE_MAGNETS_OFFER_NAME,
// seeded by migration and looked up by name because ids differ across restores).
// The client has no other signal: unit_price is $0 for the free sheet but the
// name is what tells it apart from a paid magnet sheet whose base package was
// free.
export const FREE_MAGNETS_OFFER_ADDON = 'Free Photo Magnets (offer)';

export const isFreeOfferAddon = (addon) => (addon?.name || '').trim() === FREE_MAGNETS_OFFER_ADDON;

// The cart line title for the free sheet's group.
export const freeOfferLineTitle = (addon) => `${(addon?.name || '').trim()} — $0`;

// Must this sheet survive a builder's blanket "destroy every sheet and replace
// with the new selection"?
//
// Yes for a sheet the OFFER put there: that wipe is meant to replace what the
// CUSTOMER chose, and nothing re-attaches the free magnet sheet afterwards --
// Cart#sync_free_magnets! needs a pose_id it can only get from the magnet page,
// and refresh_promo_offer! fires on order-package changes, not on addon-sheet
// destruction. So a customer who accepted their free magnets and then bought
// any add-on lost them silently, and production printed nothing.
//
// ONE implementation, because the wipe is written out in BOTH
// IndividualPrintsBuilder and AddonSheetBuilder and the rule has to hold in
// each. `editingSheetId` is the single-sheet edit case: there the builder is
// deliberately replacing one identified sheet, so nothing is protected.
// Defined before survivesSheetReplacement, which reads it (see the free-item
// section below for what it means).
export const isGrantedSheet = (sheet) => sheet?.grantedByCouponId != null && sheet.grantedByCouponId !== '';

export const survivesSheetReplacement = (sheet, { editingSheetId = null } = {}) => (
  !editingSheetId && (isFreeOfferAddon(sheet?.addon) || isGrantedSheet(sheet))
);

// ---- Free item of your choice (#5747) ---------------------------------------
//
// A sheet an offer GRANTED carries the coupon that granted it
// (`grantedByCouponId`, server-stamped, read-only). That is THE marker, never
// the add-on's name: the free item is a catalogue row other families pay for
// (a free Weekly Dry Erase Magnet and a $29.95 one are the same add-on), and a
// paid sheet on a $0 base package stamps $0 too, so neither name nor price can
// tell them apart. The magnet tier's by-name test above stays until that tier
// folds into the same type.
// (isGrantedSheet is defined above survivesSheetReplacement.)

// The cart line title for a granted sheet: "Weekly Dry Erase Magnet - School —
// FREE with your package". The line carries its real price ($29.95) and the
// coupon row below takes it off, so the parent sees what the gift was worth.
export const grantedLineTitle = (sheet) => `${(sheet?.addon?.name || '').trim()} — FREE with your package`;

// ONE question the cart asks of every sheet: did an offer put this here?
// Covers both the magnet tier (by name) and the free-item offer (by marker).
export const isOfferSheet = (sheet) => isGrantedSheet(sheet) || isFreeOfferAddon(sheet?.addon);

// The cart line title for whichever kind of offer sheet this is.
export const offerLineTitle = (sheet) => (
  isGrantedSheet(sheet) ? grantedLineTitle(sheet) : freeOfferLineTitle(sheet?.addon)
);

// ---- Replacing a package's add-on sheets without losing a granted one -------
//
// Two builders (the gifts Edit page and AddonSheetBuilder) save by marking
// EVERY existing sheet for destruction and re-creating the set from the
// builder's list. A re-created sheet is a NEW row: no `grantedByCouponId`, and
// -- now that a granted sheet is stamped at list price and only its coupon
// makes it free -- a $29.95 charge for the gift the family was promised. So a
// granted sheet is never destroyed and re-created: it is KEPT and its pose and
// background are updated in place from the builder's matching entry.
//
// ONE implementation for both builders. Pure: it only sorts the inputs into
// what to keep (with the params to copy onto it), what to destroy, and what to
// create; the callers do the saving.
//
// Matching: the builder's entries start life as `addonSheetToSheet(sheet)`, so
// an existing sheet's entry still carries its `id`; a quantity bump appends
// entries with no id. A granted sheet matches its own id first, else the first
// unclaimed entry for the same add-on (the builder may have rebuilt the list
// from image types). No entry at all means the parent removed it: destroyed.
export function splitSheetReplacement(oldSheets, newParams) {
  const unclaimed = [...(newParams || [])];
  const kept = [];
  const destroy = [];

  (oldSheets || []).forEach((sheet) => {
    if (!isGrantedSheet(sheet)) {
      destroy.push(sheet);
      return;
    }

    let at = unclaimed.findIndex((p) => p?.id != null && String(p.id) === String(sheet.id));
    if (at < 0) {
      at = unclaimed.findIndex((p) => p?.id == null
        && String(p?.imageTypeId ?? p?.addonId ?? p?.imageType?.id) === String(sheet.addonId));
    }

    if (at < 0) {
      destroy.push(sheet);
      return;
    }

    kept.push({ sheet, params: unclaimed[at] });
    unclaimed.splice(at, 1);
  });

  return { kept, destroy, create: unclaimed };
}

// Copy the builder's choice onto the kept sheet model, in place. Only the
// fields a builder can change; the marker and the price stamp are the
// server's.
export function applyKeptSheet({ sheet, params }) {
  const s = sheet;
  s.isMarkedForDestruction = false;
  if (params.poseId != null) s.poseId = params.poseId;
  if (params.pose) s.pose = params.pose;
  if (params.backgroundId !== undefined) s.backgroundId = params.backgroundId;
  if (params.background !== undefined) s.background = params.background;
  if (params.foregroundId !== undefined) s.foregroundId = params.foregroundId;
  return s;
}
